import {
  Container,
  Title,
  Stack,
  Text,
  Paper,
  Accordion,
  Button,
  Group,
  Divider,
  List,
  ThemeIcon,
  Grid,
  Card,
} from '@mantine/core';
import {
  IconHelp,
  IconMail,
  IconRocket,
  IconBox,
  IconClipboardCheck,
  IconUsers,
  IconMapPin,
  IconQrcode,
  IconReportAnalytics,
  IconCreditCard,
  IconSettings,
  IconBrandLinkedin,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

export function HelpPage() {
  const navigate = useNavigate();

  return (
    <Container size="lg">
      <Stack gap="lg">
        <div>
          <Title order={1}>Help & Support</Title>
          <Text c="dimmed" size="sm">
            Learn how to get the most out of TrustPuma asset management
          </Text>
        </div>

        {/* Quick Actions */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card
              withBorder
              padding="lg"
              h="100%"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/dashboard')}
            >
              <Stack align="center" gap="xs">
                <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
                  <IconRocket size={24} />
                </ThemeIcon>
                <Text fw={600} size="sm" ta="center">
                  Getting Started
                </Text>
                <Text size="xs" c="dimmed" ta="center">
                  Follow the onboarding checklist
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card
              withBorder
              padding="lg"
              h="100%"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/settings')}
            >
              <Stack align="center" gap="xs">
                <ThemeIcon size="xl" radius="xl" variant="light" color="green">
                  <IconSettings size={24} />
                </ThemeIcon>
                <Text fw={600} size="sm" ta="center">
                  Account Settings
                </Text>
                <Text size="xs" c="dimmed" ta="center">
                  Manage billing & preferences
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card
              withBorder
              padding="lg"
              h="100%"
              component="a"
              href="mailto:support@trustpuma.com"
              style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
            >
              <Stack align="center" gap="xs">
                <ThemeIcon size="xl" radius="xl" variant="light" color="orange">
                  <IconMail size={24} />
                </ThemeIcon>
                <Text fw={600} size="sm" ta="center">
                  Contact Support
                </Text>
                <Text size="xs" c="dimmed" ta="center">
                  support@trustpuma.com
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Getting Started Guide */}
        <Paper withBorder p="md">
          <Stack gap="md">
            <div>
              <Group>
                <IconRocket size={24} />
                <Text fw={600} size="lg">
                  Getting Started
                </Text>
              </Group>
              <Text c="dimmed" size="sm" mt="xs">
                Follow these steps to set up your asset management system
              </Text>
            </div>

            <Divider />

            <List spacing="md" size="sm">
              <List.Item
                icon={
                  <ThemeIcon color="blue" size={24} radius="xl">
                    1
                  </ThemeIcon>
                }
              >
                <Text fw={500}>Add your first asset</Text>
                <Text size="sm" c="dimmed">
                  Go to Assets and click "Add Asset" to create your first tracked item with tag ID, description, and details
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <ThemeIcon color="blue" size={24} radius="xl">
                    2
                  </ThemeIcon>
                }
              >
                <Text fw={500}>Create locations</Text>
                <Text size="sm" c="dimmed">
                  Set up your locations hierarchy in Settings → Locations (Buildings, Floors, Rooms)
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <ThemeIcon color="blue" size={24} radius="xl">
                    3
                  </ThemeIcon>
                }
              >
                <Text fw={500}>Invite your team</Text>
                <Text size="sm" c="dimmed">
                  Add team members in Settings → Users and assign them roles (Admin, Manager, or custom roles)
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <ThemeIcon color="blue" size={24} radius="xl">
                    4
                  </ThemeIcon>
                }
              >
                <Text fw={500}>Generate QR codes</Text>
                <Text size="sm" c="dimmed">
                  Print QR code labels for your assets to enable fast mobile scanning and lookups
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <ThemeIcon color="blue" size={24} radius="xl">
                    5
                  </ThemeIcon>
                }
              >
                <Text fw={500}>Run reports</Text>
                <Text size="sm" c="dimmed">
                  Use Custom Reporting to create asset reports, track depreciation, and schedule automated reports
                </Text>
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* FAQs */}
        <Paper withBorder p="md">
          <Stack gap="md">
            <div>
              <Group>
                <IconHelp size={24} />
                <Text fw={600} size="lg">
                  Frequently Asked Questions
                </Text>
              </Group>
            </div>

            <Divider />

            <Accordion variant="separated">
              <Accordion.Item value="assets">
                <Accordion.Control icon={<IconBox size={16} />}>
                  How do I add and manage assets?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    Navigate to Assets and click "Add Asset". Fill in the required fields including Tag ID,
                    Description, Brand, and Model. You can assign assets to locations, categories, departments,
                    and people. Use bulk actions to manage multiple assets at once, and create custom fields
                    in Settings → Custom Fields for additional tracking needs.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="qr-codes">
                <Accordion.Control icon={<IconQrcode size={16} />}>
                  How do QR codes work?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    Each asset has a unique QR code that links directly to its details. From the Assets page,
                    select assets and use "Print QR Codes" to generate printable labels. Anyone with the QR
                    code can scan it to view asset information (public access via secure token). Use the
                    mobile-friendly interface to scan QR codes for quick asset lookups.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="audits">
                <Accordion.Control icon={<IconClipboardCheck size={16} />}>
                  What are audits and how do I use them?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    Audits help you verify asset locations and conditions. Go to Audits to create a new audit
                    campaign, select which assets or locations to include, and assign team members. During an
                    audit, team members scan or manually verify each asset, marking them as Found, Not Found,
                    or Damaged. View audit results and history in the Audits section.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="permissions">
                <Accordion.Control icon={<IconUsers size={16} />}>
                  How do user roles and permissions work?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    TrustPuma includes three built-in roles: Admin (full access), Manager (can manage assets
                    and people), and Member (view and basic edit). You can also create custom roles in
                    Settings → Roles with specific permissions for each feature. Invite users in Settings → Users
                    and assign them the appropriate role for their responsibilities.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="locations">
                <Accordion.Control icon={<IconMapPin size={16} />}>
                  How do I organize assets by location?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    Set up your location hierarchy in Settings → Locations. Create buildings, then floors within
                    buildings, and rooms within floors. When adding or editing assets, assign them to specific
                    locations. Filter and search assets by location on the Assets page, and run location-based
                    reports to see asset distribution across your organization.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="reports">
                <Accordion.Control icon={<IconReportAnalytics size={16} />}>
                  How do I generate and schedule reports?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    Use Custom Reporting in the left menu to access report presets, create custom reports,
                    and view saved reports. Filter by status, category, location, date range, and more.
                    Export reports as CSV or PDF. Set up Report Automations to schedule reports that
                    automatically run and email to your team on a daily, weekly, or monthly basis.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="billing">
                <Accordion.Control icon={<IconCreditCard size={16} />}>
                  How do I manage my subscription and billing?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    Go to Settings → Account & Billing to view your current plan, usage limits, and
                    subscription status. Click "Manage Billing & Payment Methods" to access the Stripe
                    billing portal where you can update payment methods, view invoices, and change your
                    subscription plan. Your plan limits (assets, users, custom fields, scheduled reports)
                    are shown on this page.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="custom-fields">
                <Accordion.Control icon={<IconSettings size={16} />}>
                  What are custom fields?
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm">
                    Custom fields let you track additional information beyond the standard asset fields.
                    Create them in Settings → Custom Fields, choosing from text, number, date, dropdown,
                    checkbox, or URL field types. Assign custom fields to specific categories so they only
                    appear on relevant assets. Custom field values are included in reports and exports.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Stack>
        </Paper>

        {/* Billing & Subscription */}
        <Paper withBorder p="md">
          <Stack gap="md">
            <div>
              <Group>
                <IconCreditCard size={24} />
                <Text fw={600} size="lg">
                  Billing & Subscription
                </Text>
              </Group>
              <Text c="dimmed" size="sm" mt="xs">
                Manage your plan, payments, and invoices
              </Text>
            </div>

            <Divider />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="sm">
                  <Text fw={500} size="sm">Your Plan Includes:</Text>
                  <List size="sm" spacing="xs">
                    <List.Item>Asset tracking with QR codes</List.Item>
                    <List.Item>Custom fields and categories</List.Item>
                    <List.Item>Team member management</List.Item>
                    <List.Item>Custom reporting and exports</List.Item>
                    <List.Item>Scheduled report automations</List.Item>
                    <List.Item>Audit campaigns</List.Item>
                  </List>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="sm">
                  <Text fw={500} size="sm">Billing Options:</Text>
                  <List size="sm" spacing="xs">
                    <List.Item>View current plan and usage limits</List.Item>
                    <List.Item>Update payment methods</List.Item>
                    <List.Item>Download invoices and receipts</List.Item>
                    <List.Item>Upgrade or change your plan</List.Item>
                    <List.Item>Cancel subscription</List.Item>
                  </List>
                </Stack>
              </Grid.Col>
            </Grid>

            <Button
              variant="light"
              leftSection={<IconSettings size={16} />}
              onClick={() => navigate('/settings')}
            >
              Go to Account & Billing Settings
            </Button>

            <Text size="xs" c="dimmed">
              All billing is securely processed through Stripe. Your payment information is never stored on our servers.
            </Text>
          </Stack>
        </Paper>

        {/* Support Contact */}
        <Paper withBorder p="md">
          <Stack gap="md">
            <div>
              <Group>
                <IconMail size={24} />
                <Text fw={600} size="lg">
                  Need More Help?
                </Text>
              </Group>
              <Text c="dimmed" size="sm" mt="xs">
                Our team is here to help you succeed with TrustPuma
              </Text>
            </div>

            <Divider />

            <Group>
              <Button
                leftSection={<IconMail size={16} />}
                component="a"
                href="mailto:support@trustpuma.com"
              >
                Email Support
              </Button>
              <Button
                leftSection={<IconBrandLinkedin size={16} />}
                variant="light"
                component="a"
                href="https://www.linkedin.com/company/trustpuma"
                target="_blank"
              >
                Follow on LinkedIn
              </Button>
            </Group>

            <Text size="xs" c="dimmed">
              Response time: Usually within 24 hours on business days
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
